import { insightsIntl } from "../strings/intl";
import { type ReferencedChartTabProps, type ReferencedMultiChartDataPoint } from "@wayflyer/flyui-dataviz";
import { type IntlShape, useIntl } from "react-intl";
import * as React from "react";
import { useLoaderData } from "react-router";

import { Dimensions, MetricTypes } from "../autogen/metrics";
import { metricDescription, metricTooltip } from "../strings/metric-messages";
import type { InsightsLoaderReturn } from "../loader";
import { formatValue } from "../strings/type-formatting";
import { getUrls } from "../urls";
import { allHistoricalData, yoyReferenceForTimeRange } from "../utils/custom-time-range-helpers";
import { dateRangePresets, useDateAndTemp } from "../utils/date";
import { type DashboardOutput, getDashboardOutput } from "../utils/fetch-api";
import fetchDashboardMetaData, { type DashboardMetadata, getEmptyMetadata } from "../utils/fetch-dashboard-metadata";
import type { MetricType } from "../utils/types";

import { INTERNAL_REV_METRIC } from "./constants";
import { InternalRevenueScreen } from "./components/internal-revenue-screen";
import { aggregateRevenueSummaryData, getRevenueChartData } from "./revenue-datapoints";

const getInputMetrics = () => {
  return [
    {
      title: metricDescription(insightsIntl, INTERNAL_REV_METRIC),
      metricType: INTERNAL_REV_METRIC,
    },
  ];
};

const getTableTabs = (intl: IntlShape) => {
  return [
    {
      title: metricDescription(insightsIntl, INTERNAL_REV_METRIC),
      tooltip: metricTooltip(intl, INTERNAL_REV_METRIC),
      metricType: INTERNAL_REV_METRIC,
    },
  ];
};

const getSummaryChartTabs = (intl: IntlShape) => {
  return [
    {
      title: metricDescription(insightsIntl, INTERNAL_REV_METRIC),
      tooltip: metricTooltip(intl, INTERNAL_REV_METRIC),
      metricType: INTERNAL_REV_METRIC,
    },
  ];
};

/**
 * Entry point
 */
const InternalRevenue = () => {
  const intl = useIntl();
  const { customerDomain }: InsightsLoaderReturn = useLoaderData();
  const { dashboardDataPath } = getUrls(customerDomain);
  const [chartsData, setChartsData] = React.useState<ReferencedMultiChartDataPoint[]>([]);
  const [percentageChartsData, setPercentageChartsData] = React.useState<ReferencedMultiChartDataPoint[]>([]);

  const [rawCurrentData, setRawCurrentData] = React.useState<DashboardOutput | null>(null);
  const [rawReferenceData, setRawReferenceData] = React.useState<DashboardOutput | null>(null);
  const [rawChartData, setRawChartData] = React.useState<DashboardOutput | null>(null);

  const [requiresDataComputation, setRequiresDataComputation] = React.useState(false);
  const currencyCodeRef = React.useRef("USD");

  const [groups, setGroups] = React.useState<string[]>([]);
  const [activeGroups, setActiveGroups] = React.useState<string[]>([]);
  const [initialActiveGroups, setInitialActiveGroups] = React.useState(true);
  const [groupMapping, setGroupMapping] = React.useState<Record<string, number>>({});

  const [requiresDataFetch, setRequiresDataFetch] = React.useState(true);
  const [isLoadingRawData, setIsLoadingRawData] = React.useState(false);

  const [selectedCurrentTimeRangeEnabled, setSelectedCurrentTimeRangeEnabled] = React.useState(true);
  const [selectedReferenceTimeRangeEnabled, setSelectedReferenceTimeRangeEnabled] = React.useState(false);

  const [customerMetadata, setCustomerMetadata] = React.useState<DashboardMetadata>(getEmptyMetadata());

  const [summaryData, setSummaryData] = React.useState<ReferencedChartTabProps[]>(getSummaryChartTabs(intl));

  const allRawData = React.useMemo(
    () => [customerMetadata, rawChartData, rawCurrentData, rawReferenceData],
    [customerMetadata, rawCurrentData, rawReferenceData, rawChartData]
  );

  const formatChartValue = (value: number | string | readonly (number | string)[]) => {
    const metricType = { type: "CURRENCY", currencyCode: currencyCodeRef.current } as MetricType;
    return formatValue({ value: value.toString(), metricType });
  };

  const formatPercentValue = (value: number | string | readonly (number | string)[]) => {
    const metricType = { type: "PERCENTAGE" } as MetricType;
    return formatValue({ value: value.toString(), metricType });
  };

  React.useEffect(() => {
    if (allRawData.every((x) => x)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setIsLoadingRawData(false);
    }
  }, [allRawData]);

  const onChange = () => {
    setRequiresDataFetch(true);
  };

  const {
    selectedTimeRange,
    handleTimeRangeChange,
    currentPreset,
    temporalResolution,
    selectedButtonIndex,
    hideWeekly,
    hideMonthly,
    handleTempResChange,
  } = useDateAndTemp(onChange, onChange);

  const addTotalGroup = (output: DashboardOutput): DashboardOutput => {
    output.groups.push(["Total"]);

    output.data = output.data.map((dataPoint) => {
      dataPoint["Total" + MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX] = Object.keys(
        dataPoint
      ).reduce((total, key) => {
        if (key !== "day") {
          return total + dataPoint[key];
        } else {
          return total;
        }
      }, 0);
      return dataPoint;
    });

    output.summary["Total" + MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX] = Object.keys(
      output.summary
    ).reduce((total, key) => {
      if (key !== "day") {
        return total + output.summary[key];
      } else {
        return total;
      }
    }, 0);

    return output;
  };

  const fetchCurrentSummaryData = React.useCallback(() => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: [INTERNAL_REV_METRIC],
          filename: "revenue_insights",
          groupBys: [Dimensions.STORE_ID],
        },
      ],
      selectedTimeRange,
      (data) => {
        setRawCurrentData(addTotalGroup(data));
      },
      null,
      () => {
        setIsLoadingRawData(false);
      },
      temporalResolution
    );
  }, [dashboardDataPath, selectedTimeRange, temporalResolution]);

  const fetchReferenceSummaryData = React.useCallback(() => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: [INTERNAL_REV_METRIC],
          filename: "revenue_insights",
          groupBys: [Dimensions.STORE_ID],
        },
      ],
      yoyReferenceForTimeRange(selectedTimeRange, currentPreset),
      (data) => {
        setRawReferenceData(addTotalGroup(data));
      },
      null,
      () => {
        setIsLoadingRawData(false);
      },
      temporalResolution
    );
  }, [dashboardDataPath, selectedTimeRange, temporalResolution, currentPreset]);

  const fetchChartData = React.useCallback(() => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: [INTERNAL_REV_METRIC],
          filename: "revenue_insights",
          groupBys: [Dimensions.STORE_ID],
        },
      ],
      allHistoricalData(selectedTimeRange),
      (data) => {
        const d = addTotalGroup(data);
        setRawChartData(d);
        currencyCodeRef.current = d.currencyCode;
        setGroups(d.groups.map((x) => x[0]));

        if (initialActiveGroups) {
          setInitialActiveGroups(false);
          setActiveGroups(d.groups.map((x) => x[0]));

          const g = {};
          d.groups.forEach((val, index) => (g[val[0]] = index));
          setGroupMapping(g);
        }
      },
      null,
      () => {
        setIsLoadingRawData(false);
      },
      temporalResolution
    );
  }, [dashboardDataPath, selectedTimeRange, temporalResolution, initialActiveGroups]);

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setIsLoadingRawData(true);

    fetchDashboardMetaData(
      dashboardDataPath,
      (data) => {
        setCustomerMetadata(data);
      },
      () => {}
    );
  }, [dashboardDataPath]);

  React.useEffect(() => {
    if (requiresDataFetch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setRawCurrentData(null);
      setRawReferenceData(null);
      setRawChartData(null);

      setIsLoadingRawData(true);

      fetchReferenceSummaryData();
      fetchCurrentSummaryData();
      fetchChartData();

      setRequiresDataFetch(false);
      setRequiresDataComputation(true);
    }
  }, [requiresDataFetch, fetchReferenceSummaryData, fetchCurrentSummaryData, fetchChartData]);

  const computeRevenueData = () => {
    if (
      !requiresDataComputation ||
      requiresDataFetch ||
      !rawChartData ||
      !rawCurrentData ||
      !rawReferenceData ||
      isLoadingRawData ||
      !groups.length
    ) {
      return;
    }
    const chartAndTableData = getRevenueChartData(
      selectedTimeRange,
      rawChartData,
      temporalResolution,
      getInputMetrics(),
      getTableTabs(intl),
      groups
    );

    setChartsData(chartAndTableData.chartData);

    const summaryData: ReferencedChartTabProps[] = aggregateRevenueSummaryData(
      rawCurrentData,
      rawReferenceData,
      selectedTimeRange,
      getSummaryChartTabs(intl),
      groups
    );

    summaryData.forEach((summaryPanel) => {
      // summaryPanel["selected"] = activeGroups.indexOf(summaryPanel["title"] > -1);
      // @ts-expect-error - this looks like a bug and perhaps should be ^
      summaryPanel["selected"] = activeGroups.indexOf(summaryPanel["title"] > -1);
    });

    setSummaryData(summaryData);

    const percentageData = chartAndTableData.chartData.map((dataPoint) => {
      const rv: ReferencedMultiChartDataPoint = { day: "" };
      Object.keys(dataPoint).forEach((key) => {
        if (key === "day" || key === "dayRaw") {
          rv[key] = dataPoint[key];
        } else if (!key.startsWith("Total") && !key.endsWith("Reference")) {
          // Split into StoreID - [Current/Reference]
          const d = key.split(MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX);

          // Get actual percentage
          const percentage =
            dataPoint[key] /
            dataPoint[`Total${MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX}${d[1]}`];

          // Ensure they sum to <100 for graph aesthetic purposes
          rv[key] = Math.floor(1000 * percentage) / 10;
        }
      });
      return rv;
    });
    setPercentageChartsData(percentageData);

    setRequiresDataComputation(false);
  };
  computeRevenueData();

  const handleThisYearSwitch = (isOn: boolean) => {
    setSelectedCurrentTimeRangeEnabled(isOn);
  };

  const handleLastYearSwitch = (isOn: boolean) => {
    setSelectedReferenceTimeRangeEnabled(isOn);
  };

  const onSelectSummaryTab = (group: string, checked: boolean) => {
    if (checked && activeGroups.indexOf(group) < 0) {
      const newActive = groups.filter((c) => c === group || activeGroups.indexOf(c) > -1);
      setActiveGroups(newActive);
    } else {
      setActiveGroups(activeGroups.filter((c) => c !== group));
    }
  };

  const stripeHint = "Stripe revenue may be lower than expected due to de-duplication of orders across platforms.";
  const hint = groups.some((group) => group.startsWith("Stripe")) ? stripeHint : "";

  return (
    <InternalRevenueScreen
      hint={hint}
      pageTitle="Gross Revenue"
      chartData={chartsData}
      percentageChartData={percentageChartsData}
      groups={activeGroups}
      groupMapping={groupMapping}
      formatValue={formatChartValue}
      formatPercentageValue={formatPercentValue}
      summaryData={summaryData}
      onSelectSummaryTab={onSelectSummaryTab}
      datePickerHeaderProps={{
        currentPreset,
        startDate: selectedTimeRange.start.format("YYYY-MM-DD"),
        endDate: selectedTimeRange.end.format("YYYY-MM-DD"),
        dateRangePresets: dateRangePresets,
        onChange: (dateRangePreset, startDate, endDate) =>
          handleTimeRangeChange(dateRangePreset as Parameters<typeof handleTimeRangeChange>[0], startDate, endDate),
      }}
      chartFilters={{
        switchOneLabel: insightsIntl.formatMessage({ id: "chart filter - This year", defaultMessage: "This year" }),
        switchTwoLabel: insightsIntl.formatMessage({ id: "chart filter - Last year", defaultMessage: "Last year" }),
        switchesDisabled: isLoadingRawData,
        hideWeekly,
        hideMonthly,
        onSwitchOne: handleThisYearSwitch,
        onSwitchTwo: handleLastYearSwitch,
        onClickDateRes: handleTempResChange,
        selectedButtonIndex: selectedButtonIndex,
      }}
      thisYearEnabled={selectedCurrentTimeRangeEnabled}
      lastYearEnabled={selectedReferenceTimeRangeEnabled}
    />
  );
};

export default InternalRevenue;
